import { ref, computed } from 'vue'
import { API } from '../api/client'

interface AuthUser {
  id: string
  username: string
  is_admin: boolean
  created_at: string
}

const token = ref<string | null>(null)
const refreshToken = ref<string | null>(null)
const user = ref<AuthUser | null>(null)
let authInitialized = false
let authInitializationPromise: Promise<void> | null = null

function saveAuthState() {
  if (token.value) {
    localStorage.setItem('auth_token', token.value)
  } else {
    localStorage.removeItem('auth_token')
  }

  if (refreshToken.value) {
    localStorage.setItem('auth_refresh_token', refreshToken.value)
  } else {
    localStorage.removeItem('auth_refresh_token')
  }

  if (user.value) {
    localStorage.setItem('auth_user', JSON.stringify(user.value))
  } else {
    localStorage.removeItem('auth_user')
  }
}

export function useAuth() {
  const isAuthenticated = computed(() => !!token.value)

  async function initializeAuth() {
    if (authInitialized) {
      return
    }

    if (authInitializationPromise) {
      return authInitializationPromise
    }

    authInitializationPromise = (async () => {
      const savedToken = localStorage.getItem('auth_token')
      const savedRefreshToken = localStorage.getItem('auth_refresh_token')
      const savedUser = localStorage.getItem('auth_user')

      if (savedUser) {
        user.value = JSON.parse(savedUser)
      }

      token.value = savedToken
      refreshToken.value = savedRefreshToken

      if (savedToken) {
        API.setToken(savedToken)
      }

      if (refreshToken.value) {
        try {
          const response = await API.refreshSession(refreshToken.value)
          token.value = response.token
          refreshToken.value = response.refreshToken
          API.setToken(response.token)
          saveAuthState()
        } catch {
          await logout()
          return
        }
      }

      if (token.value && !user.value) {
        try {
          user.value = await API.getCurrentUser()
          saveAuthState()
        } catch {
          await logout()
          return
        }
      }

      authInitialized = true
      authInitializationPromise = null
    })()

    return authInitializationPromise
  }

  async function login(username: string, password: string): Promise<void> {
    try {
      const response = await API.login(username, password)
      token.value = response.token
      refreshToken.value = response.refreshToken
      user.value = response.user

      API.setToken(response.token)
      saveAuthState()
    } catch (error) {
      token.value = null
      refreshToken.value = null
      user.value = null
      saveAuthState()
      throw error
    }
  }

  async function register(username: string, password: string): Promise<void> {
    try {
      const response = await API.register(username, password)
      token.value = response.token
      refreshToken.value = response.refreshToken
      user.value = response.user

      API.setToken(response.token)
      saveAuthState()
    } catch (error) {
      token.value = null
      refreshToken.value = null
      user.value = null
      saveAuthState()
      throw error
    }
  }

  async function logout(): Promise<void> {
    try {
      await API.logout(refreshToken.value)
    } catch {
      // Ignore failed logout call, clear local state anyway
    }

    token.value = null
    refreshToken.value = null
    user.value = null
    API.setToken(null)
    saveAuthState()
  }

  function clearMemoryState() {
    token.value = null
    refreshToken.value = null
    user.value = null
    API.setToken(null)
    // Intentionally does NOT call saveAuthState() or hit the backend —
    // localStorage is preserved so the session can be recovered later
    // (e.g. on next page load via initializeAuth).
  }

  return {
    isAuthenticated,
    user: computed(() => user.value),
    login,
    register,
    logout,
    clearMemoryState,
    initializeAuth,
  }
}
